import { useCallback, useEffect, useRef } from 'react';

/** MM-842: space kept between the sticky app bar and a section that is too tall to centre. */
const TOP_GAP = 8;

/**
 * MM-842: how long an opened section keeps being re-aligned while its content loads and grows
 * (spinner → form). After this, or as soon as the user scrolls or types, it is left alone.
 */
const FOLLOW_MS = 4000;

/** MM-842: user input that means "I am scrolling myself", which stops the automatic re-alignment. */
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'mousedown'];

/**
 * MM-842: height of the sticky app bar (MUI AppBar renders a <header>), so a section is never hidden
 * underneath it. Falls back to the desktop toolbar height when no header is on the page.
 */
const getStickyHeaderHeight = (): number => document.querySelector('header')?.getBoundingClientRect().height ?? 64;

/**
 * MM-842: scrolls an opened profile section so it is fully in view without the user scrolling again.
 * A section that fits below the app bar is centred in the visible area; a taller one is placed with its
 * header just under the app bar. The section is watched for size changes for a short while after it opens,
 * because its form loads asynchronously, and is re-aligned as it grows.
 *
 * @returns registerSection - ref callback to attach to each section, keyed by section key
 *          scrollToSection - brings the section with the given key into view
 */
export const useSectionAutoScroll = () => {
  /** DOM node of each section, keyed by section key (e.g. 'insuranceDetails'). */
  const sections = useRef<Record<string, HTMLElement | null>>({});
  /** Stops the current follow (size watcher, timer, user-input listeners); undefined when none is running. */
  const stopFollowing = useRef<() => void>();

  /**
   * Scrolls the window so the given section is centred, or top-aligned under the app bar when it is
   * taller than the visible area. MM-850: with centreMiddle, the middle of the section is placed in the
   * middle of the visible area whatever its height.
   */
  const align = (el: HTMLElement, centreMiddle: boolean) => {
    const headerHeight = getStickyHeaderHeight();
    const visibleHeight = window.innerHeight - headerHeight;
    const rect = el.getBoundingClientRect();
    const offset = centreMiddle || rect.height < visibleHeight ? (visibleHeight - rect.height) / 2 : TOP_GAP;
    window.scrollTo({ top: Math.max(0, window.scrollY + rect.top - headerHeight - offset), behavior: 'smooth' });
  };

  /**
   * Brings the section into view and keeps it aligned while it grows, until FOLLOW_MS passes or the user
   * scrolls. Opening another section replaces the previous follow. MM-850: centreMiddle keeps the middle of
   * the section in the middle of the screen, used for a section the page opened by itself.
   */
  const scrollToSection = useCallback((key: string, centreMiddle = false) => {
    stopFollowing.current?.();
    const el = sections.current[key];
    if (!el) return;

    // ResizeObserver also fires once right after observe(), which does the first alignment.
    const observer = new ResizeObserver(() => align(el, centreMiddle));
    const stop = () => {
      observer.disconnect();
      clearTimeout(timer);
      USER_SCROLL_EVENTS.forEach((type) => window.removeEventListener(type, stop));
      stopFollowing.current = undefined;
    };
    const timer = setTimeout(stop, FOLLOW_MS);
    USER_SCROLL_EVENTS.forEach((type) => window.addEventListener(type, stop, { passive: true }));
    observer.observe(el);
    stopFollowing.current = stop;
  }, []);

  /** Returns the ref callback that records a section's DOM node under its key. */
  const registerSection = useCallback(
    (key: string) => (el: HTMLElement | null) => {
      sections.current[key] = el;
    },
    [],
  );

  /** Stops any follow still running when the page unmounts. */
  useEffect(() => () => stopFollowing.current?.(), []);

  return { registerSection, scrollToSection };
};
